import React, { useEffect, useState } from 'react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useScreenWidth from '~/hooks/useScreenWidth';

// Actions
import { failure, success } from '~/redux/actions/snackbar-actions';
import {
  DESIGNER_QUERY_STATUS,
  submitTemplateQueryForApproval,
} from '~/redux/actions/admin-custom-designer-query.js';
import { fetchAdminQueries, fetchAdminQueryDetails } from '~/redux/actions/admin-query-actions.js';

// Utils
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION } from '~/utils/constants.js';
import { dateFormat } from '~/utils/date-format.js';
import { stringLength } from '~/utils/helperFunctions.js';
import { displayTooltip } from '~/hooks/displayTooltip.js';
import { MESSAGES } from '~/utils/message';

// Components
import ConfirmModal from '~/components/General/ConfirmModal';
import QueryDetails from '../QueryDetails';
import OlcTablePagination from '~/components/General/OlcTablePagination';

// Mui Components
import { Box, IconButton, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import DataGrid from '~/components/General/OlcDataGrid';
import OlcActionMenu from '~/components/General/OlcActionMenu';

// Icons
import { Edit4CIcon, InfoWebhookCIcon, NoContactCIcon } from '~/assets/images';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Publish from '~/assets/images/admin/stack.svg';
import Start from '~/assets/images/admin/play.svg';
import Details from '~/assets/images/admin/eye.svg';

// Styles
import './styles.scss';

const SUBMIT_FOR_APPROVAL_STATUSES = [
  DESIGNER_QUERY_STATUS.IN_PROGRESS,
  DESIGNER_QUERY_STATUS.REVISION_REQUESTED,
];

const LOCKED_TEMPLATE_STATUSES = [
  DESIGNER_QUERY_STATUS.PUBLISHED,
  DESIGNER_QUERY_STATUS.SUBMITTED_FOR_APPROVAL,
  DESIGNER_QUERY_STATUS.AWAITING_APPROVAL,
];

const SUBMIT_APPROVAL_DIALOG_INITIAL_STATE = {
  open: false,
  message: '',
  loading: false,
  heading: '',
  successButtonName: '',
};

const QueriesListing = (props) => {
  const { queries, updatePagination, pagination, searchApplied } = props;
  const isTooltip = displayTooltip();
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [activeRowId, setActiveRowId] = useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [dialog, setDialog] = useState(SUBMIT_APPROVAL_DIALOG_INITIAL_STATE);
  const openMenu = Boolean(anchorEl);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pageSizes = DEFAULT_PAGE_SIZES;
  const queryDetails = useSelector((state) => state.adminQueriesReducers.queryDetails);

  const queryDetailsLoader = useSelector((state) => state.adminQueriesReducers.queryDetailsLoader);
  const rowsCount = useSelector((state) => state.adminQueriesReducers.rowsCount);
  const to = useSelector((state) => state.adminQueriesReducers.to);
  const from = useSelector((state) => state.adminQueriesReducers.from);
  const lastPage = useSelector((state) => state.adminQueriesReducers.lastPage || 1);
  const currentPage = useSelector((state) => state.adminQueriesReducers.currentPage || 1);
  const perPage = useSelector(
    (state) => state.adminQueriesReducers.perPage || DEFAULT_PAGINATION.pageSize
  );
  const isLoading = useSelector((state) => state.adminQueriesReducers.loading);

  const handleStart = (id) => {
    navigate(`/admin/designer-queries/template/${id}`);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const canContinueEditing = (status) => SUBMIT_FOR_APPROVAL_STATUSES.includes(status);
  const canStartDesign = (status) => ![
    DESIGNER_QUERY_STATUS.IN_PROGRESS,
    DESIGNER_QUERY_STATUS.REVISION_REQUESTED,
    ...LOCKED_TEMPLATE_STATUSES,
  ].includes(status);
  const canSubmitForApproval = (status) => SUBMIT_FOR_APPROVAL_STATUSES.includes(status);

  const closeSubmitApprovalDialog = () => {
    setDialog(SUBMIT_APPROVAL_DIALOG_INITIAL_STATE);
    setSelectedRow(null);
  };

  const onConfirmSubmitForApproval = async () => {
    setDialog((prev) => ({
      ...prev,
      loading: true,
    }));
    const response = await submitTemplateQueryForApproval(selectedRow);

    if (response?.status === 200) {
      closeSubmitApprovalDialog();
      dispatch(success(MESSAGES.ADMIN_DASHBOARD.QUERIES.TEMPLATE_SUBMITTED_FOR_APPROVAL));
      dispatch(fetchAdminQueries());
      return;
    }

    setDialog((prev) => ({
      ...prev,
      loading: false,
    }));
    dispatch(failure(response?.data?.message || 'Internal Server Error'));
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const openQueryDetails = (params) => {
    setOpen(true);
    dispatch(fetchAdminQueryDetails(params.id));
  };

  const closeQueryDetails = () => {
    setOpen(false);
  };

  const handleChangePage = (event, newPage) => {
    updatePagination({
      page: newPage,
      pageSize: pagination.pageSize,
    });
  };

  const useScreen = useScreenWidth();

  const PublishedTemplateIdTooltip = (params) => {
    return (
      <>
        <span className="templateNameTooltip">
          {MESSAGES.ADMIN_DASHBOARD.QUERIES.PUBLISHED_TEMPLATE_ID}
        </span>{' '}
        {params.row.templateId}
      </>
    );
  };

  const columns = [
    {
      field: 'templateName',
      headerName: 'Template Name',
      width: 205,
      sortable: false,
      flex: window.innerWidth > 1500 ? 1 : 0,
      renderCell: (params) =>
        stringLength(`${params.row.meta.templateTitle}`, 17) ? (
          <Box className="tempInfoWrapper">
            <Tooltip title={params.row.meta.templateTitle} disableHoverListener={isTooltip}>
              <Box className="tempValue">
                <Typography className="templateTitleText">{params.row.meta.templateTitle}</Typography>
                <Typography className="templateIdText">ID: {params.row.id}</Typography>
              </Box>
            </Tooltip>
            {params.row.status === 'Published' && (
              <Tooltip title={PublishedTemplateIdTooltip(params)} disableHoverListener={isTooltip}>
                <img src={InfoWebhookCIcon} alt="info" width={15} height={15} />
              </Tooltip>
            )}
          </Box>
        ) : (
          <Box className="tempInfoWrapper">
            <Box className="tempValue">
              <Typography className="templateTitleText">{params.row.meta.templateTitle}</Typography>
              <Typography className="templateIdText">ID: {params.row.id}</Typography>
            </Box>
            {params.row.status === 'Published' && (
              <Tooltip title={PublishedTemplateIdTooltip(params)} disableHoverListener={isTooltip}>
                <img src={InfoWebhookCIcon} alt="info" width={15} height={15} />
              </Tooltip>
            )}
          </Box>
        ),
    },
    {
      field: 'productType',
      headerName: 'Product Type',
      type: 'number',
      width: 205,
      sortable: false,
      flex: window.innerWidth > 1500 ? 1 : 0,
      renderCell: (params) => (
        <Box className="productValueWrapper">
          <Typography>{params.row.product.productType || '-'}</Typography>
          {params.row.product.productType === 'Postcards' && (
            <Typography>{params.row.product.paperSize}</Typography>
          )}
          {params.row.product.id === '2' && (
            <Typography>{MESSAGES.ADMIN_DASHBOARD.QUERIES.PROFESSIONAL_WINDOWED}</Typography>
          )}
          {params.row.product.id === '4' && (
            <Typography>{MESSAGES.ADMIN_DASHBOARD.QUERIES.PROFESSIONAL_NON_WINDOWED}</Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 205,
      sortable: false,
      flex: window.innerWidth > 1500 ? 1 : 0,
      renderCell: (params) =>
        stringLength(`${params.row.email}`, 21) ? (
          <Tooltip title={params.row.email} disableHoverListener={isTooltip}>
            <Box className="productValueWrapper">
              <Typography>{params.row.email || '-'}</Typography>
            </Box>
          </Tooltip>
        ) : (
          <Box className="productValueWrapper">
            <Typography>{params.row.email || '-'}</Typography>
          </Box>
        ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 205,
      sortable: false,
      flex: window.innerWidth > 1500 ? 1 : 0,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.status}</Typography>
        </Box>
      ),
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 205,
      sortable: false,
      flex: window.innerWidth > 1500 ? 1 : 0,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.source}</Typography>
        </Box>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 205,
      sortable: false,
      flex: window.innerWidth > 1500 ? 1 : 0,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{dateFormat(params.row.createdAt)}</Typography>
        </Box>
      ),
    },
    {
      field: 'updatedAt',
      headerName: 'Updated At',
      width: 205,
      sortable: false,
      flex: window.innerWidth > 1500 ? 1 : 0,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{dateFormat(params.row.updatedAt)}</Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <>
          <Box>
            <IconButton
              aria-label="more"
              id="long-button"
              aria-controls={
                openMenu && activeRowId?.id === params.row.id ? 'long-menu' : undefined
              }
              aria-expanded={openMenu && activeRowId?.id === params.row.id ? 'true' : undefined}
              aria-haspopup="true"
              onClick={(event) => {
                event.stopPropagation();
                setActiveRowId(params.row); // Set active row ID when the menu is opened
                handleClick(event);
              }}
            >
              <MoreVertIcon
                sx={{
                  color: activeRowId?.id === params.row.id && anchorEl ? '#ed5c2f' : 'inherit',
                }}
              />
            </IconButton>
            <OlcActionMenu
              id="long-menu"
              MenuListProps={{ 'aria-labelledby': 'long-button' }}
              anchorEl={anchorEl}
              open={openMenu && activeRowId?.id === params.row.id}
              onClose={handleClose}
            >
              {canContinueEditing(activeRowId?.status) && (
                <MenuItem onClick={() => handleStart(activeRowId?.id)}>
                  <img src={Edit4CIcon} alt="edit" />
                  Continue Editing
                </MenuItem>
              )}
              {canStartDesign(activeRowId?.status) && (
                <MenuItem onClick={() => handleStart(activeRowId?.id)}>
                  <img src={Start} alt="search" />
                  Start
                </MenuItem>
              )}
              <MenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  openQueryDetails(activeRowId);
                  handleClose();
                }}
              >
                <img src={Details} alt="duplicate" />
                View Details
              </MenuItem>
              {canSubmitForApproval(activeRowId?.status) && (
                <MenuItem
                  onClick={() => {
                    setSelectedRow(activeRowId);
                    handleClose();
                    setDialog({
                      open: true,
                      message: 'Are you sure you want to send this design for approval?',
                      loading: false,
                      successButtonName: 'Send',
                      heading: 'Send for Approval',
                    });
                  }}
                >
                  <img src={Publish} alt="send for approval" />
                  Send for Approval
                </MenuItem>
              )}
            </OlcActionMenu>
          </Box>
        </>
      ),
    },
  ];

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [screenWidth]);

  return (
    <>
      <div className="queryListingWrapper olcAdminListTable">
        <DataGrid
          rows={queries}
          columns={columns}
          pageSizeOptions={[10, 20, 50, 100]}
          rowCount={rowsCount}
          pagination={pagination}
          paginationMode="server"
          hideFooterSelectedRowCount
          hideFooterPagination
          rowSelection={false}
          getRowId={(row) => row?.id}
          disableColumnMenu={true}
          className="designerQueryTable"
          columnHeaderHeight={47}
          loading={isLoading}
          slots={{
            noRowsOverlay: () => (
              <Stack className="noRowsTextContacts">
                <img src={NoContactCIcon} alt="NoContact" />
                {`${MESSAGES.NO_RESULTS_TEXT} ${!searchApplied ? 'filters' : 'search'}`}
              </Stack>
            ),
            noResultsOverlay: () => (
              <Stack className="noRowsTextContacts">
                <img src={NoContactCIcon} alt="NoContact" />
                {MESSAGES.NO_ROWS_TEXT}
              </Stack>
            ),
          }}
        />
        {(isLoading || rowsCount > 0) && (
          <OlcTablePagination
            count={lastPage}
            page={currentPage}
            pageSize={perPage}
            pageSizes={pageSizes}
            onPageChange={handleChangePage}
            onPageSizeChange={(event) =>
              updatePagination({ page: 0, pageSize: event.target.value })
            }
            from={from}
            to={to}
            total={rowsCount}
            loading={isLoading}
          />
        )}
      </div>
      <QueryDetails
        open={open}
        onClose={closeQueryDetails}
        data={queryDetails}
        loading={queryDetailsLoader}
      />
      {dialog.open && (
        <ConfirmModal
          openConfirmModal={{
            open: dialog.open,
            header: dialog.heading,
            message: dialog.message,
            loading: dialog.loading,
            successButtonName: dialog.successButtonName,
            cancelButtonName: 'Cancel',
            handleClose: closeSubmitApprovalDialog,
            handleOk: onConfirmSubmitForApproval,
          }}
        />
      )}
    </>
  );
};

export default QueriesListing;
